import React, { useState, useCallback } from 'react';
import SVGImage from '../layout/SVGImage';
import { OperationManagementPowerPlantRegistrationModalTopNavigation } from './OperationManagementPowerPlantRegistrationModalTopNavigation';
import { Divider } from '../common/Divider';
import { OperationManagementPowerPlantRegistrationModalExcel } from './OperationManagementPowerPlantRegistrationModalExcel';
import SubmitButton from '../common/SubmitButton';
import { useTranslation } from 'react-i18next';
import { CancleButton } from '../common/CancleButton';
import { OperationManagementPowerPlantRegistrationModalSingleRegistration } from './OperationManagementPowerPlantRegistrationModalSingleRegistration';
import { patch, post } from '@/utils/fetch';

interface OperationManagementPowerPlantRegistrationModalProps {
  testId?: string;
  className?: string;
  isEdit?: boolean;
  onClick?: () => void;
  data?: Partial<PowerPlantListTableData> | null;
  navigationBarIndex?: number;
}

export const OperationManagementPowerPlantRegistrationModal: React.FC<
  OperationManagementPowerPlantRegistrationModalProps
> = ({
  testId,
  className,
  onClick,
  isEdit = false,
  data,
  navigationBarIndex = 0,
}) => {
  const [topNavigationBarIndex, setTopNavigationBarIndex] =
    useState(navigationBarIndex);
  const { t } = useTranslation();
  const handleFormAction = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const form = e.currentTarget;
      const formData = new FormData(form);

      const {
        administrativeAddress: address,
        contactPerson: contact,
        energyMarketId: kpxId,
        latitude,
        longitude,
        plantName: name,
        plantType,
        siteId,
      } = Object.fromEntries(formData.entries());
      const generatorIds = formData.getAll('generatorId');
      const generatorNames = formData.getAll('generatorName');
      const generatorCapacities = formData.getAll('generatorCapacity');
      const generatorTypes = formData.getAll('generatorType');
      const plants = generatorIds.map((generaotrId, index) => {
        return {
          plantId: generaotrId,
          plantName: generatorNames[index],
          capacity: generatorCapacities[index],
          class: generatorTypes[index],
        };
      });
      const genNum = generatorIds.length;
      const capacityGenMw = plants.reduce((prev, cur) => {
        const value = Number(cur.capacity);
        if (cur.class !== 'ESS') prev += isNaN(value) ? 0 : value;
        return prev;
      }, 0);
      const capacityEssMw = plants.reduce((prev, cur) => {
        const value = Number(cur.capacity);
        if (cur.class === 'ESS') prev += isNaN(value) ? 0 : value;
        return prev;
      }, 0);

      const requestBody = {
        address,
        siteId,
        contact,
        kpxId,
        plants,
        capacityEssMw,
        capacityGenMw,
        genNum,
        name,
        class: plantType,
        latitude,
        longitude,
      };

      const response = isEdit
        ? await patch('/customer/site', {
            body: JSON.stringify(requestBody),
          })
        : await post('/customer/site', {
            body: JSON.stringify(requestBody),
          });

      if (response.status === 'success') onClick?.();
    },
    []
  );

  const handleCancleButton = () => {
    if (topNavigationBarIndex === 0) onClick?.();
    else setTopNavigationBarIndex((prev) => prev - 1);
  };

  return (
    <div
      className={`flex flex-col items-end ${className}`}
      data-testid={testId}
    >
      <SVGImage
        className="mb-[15px] cursor-pointer"
        testId={`${testId}-close-button`}
        name="cancle"
        onClick={onClick}
      />
      <div
        className={`w-full bg-white p-[40px] pt-[27px] shadow-8 ${className} `}
      >
        <OperationManagementPowerPlantRegistrationModalTopNavigation
          testId={`${testId}-top-navigation`}
          selectedIndex={topNavigationBarIndex}
          setNavigationIndex={setTopNavigationBarIndex}
        />
        <Divider className="-translate-y-px transform bg-[#D6DEE4]" />
        {/* 다중등록 */}
        <form onSubmit={handleFormAction}>
          {topNavigationBarIndex === 0 ? (
            <OperationManagementPowerPlantRegistrationModalExcel
              className="mt-[30px]"
              testId={`${testId}-excel`}
            />
          ) : (
            <OperationManagementPowerPlantRegistrationModalSingleRegistration
              isEdit={isEdit}
              data={data}
              testId={`${testId}-single-registration`}
            />
          )}
          <Divider className="mt-[30px] bg-[#3F4C5A]" />
          <div className="mt-[30px] flex justify-end">
            <CancleButton
              onClick={handleCancleButton}
              className="mr-4 justify-between px-[30px] [&]:bg-dark-9"
              text={t('settlement.cancle')}
              testId={`${testId}-cancle-button`}
              disabled={false}
              submitted={false}
            />
            <SubmitButton
              className="justify-between px-[30px]"
              text={t('overview.modal.limit.button', { context: 'submit' })}
              testId={`${testId}-submit-button`}
              disabled={false}
              submitted={false}
            />
          </div>
        </form>
      </div>
    </div>
  );
};
